import { createAction, createReducer } from "@reduxjs/toolkit"
import { api } from "./helpers/api"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"

const requestedGuideProfile = createAction("clients/requestedGuideProfile")
const receivedGuideProfile = createAction("clients/receivedGuideProfile")
const failedGuideProfile = createAction("clients/failedGuideProfile")
const cleanedGuideProfile = createAction("guides/cleanedGuideProfile")

const requestedGuideAbout = createAction("clients/requestedGuideAbout")
const receivedGuideAbout = createAction("clients/receivedGuideAbout")
const failedGuideAbout = createAction("clients/failedGuideAbout")

const requestedGuideExperiences = createAction("clients/requestedGuideExperiences")
const receivedGuideExperiences = createAction("clients/receivedGuideExperiences")
const failedGuideExperiences = createAction("clients/failedGuideExperiences")

const requestedGuideReviews = createAction("guide-profile/requestedGuideReviews")
const receivedGuideReviews = createAction("guide-profile/receivedGuideReviews")
const failedGuideReviews = createAction("guide-profile/failedGuideReviews")

const requestedGuideBoats = createAction("clients/requestedGuideBoats")
const receivedGuideBoats = createAction("clients/receivedGuideBoats")
const failedGuideBoats = createAction("clients/failedGuideBoats")

const requestedGuideLocation = createAction("clients/requestedGuideLocation")
const receivedGuideLocation = createAction("clients/receivedGuideLocation")
const failedGuideLocation = createAction("clients/failedGuideLocation")

// Prompt List actions
const requestedPromptList = createAction("promptList/requested")
const receivedPromptList = createAction("promptList/received")
const failedPromptList = createAction("promptList/failed")

const initialState = {
  promptList: {
    ...LOADING_INITIAL_STATE,
    data: []
  },
  guide: {
    ...LOADING_INITIAL_STATE,
    data: {}
  },
  about: {
    ...LOADING_INITIAL_STATE,
    data: {}
  },
  experiences: {
    ...LOADING_INITIAL_STATE,
    data: []
  },
  boats: {
    ...LOADING_INITIAL_STATE,
    data: []
  },
  location: {
    ...LOADING_INITIAL_STATE,
    data: {}
  },
  reviews: {
    ...LOADING_INITIAL_STATE,
    data: {}
  }
}

const guideProfileReducer = createReducer(initialState, {
  [requestedPromptList.type]: (state) => {
    setRequested(state.promptList)
  },
  [receivedPromptList.type]: (state, action) => {
    setReceived(state.promptList)
    state.promptList.data = action.payload
  },
  [failedPromptList.type]: (state) => {
    setFailed(state.promptList)
  },
// Thunk for fetching prompt list
  [requestedGuideProfile.type]: (state) => {
    setRequested(state.guide)
  },
  [receivedGuideProfile.type]: (state, action) => {
    setReceived(state.guide)
    state.guide.data = action.payload
  },
  [failedGuideProfile.type]: (state) => {
    setFailed(state.guide)
  },
  [cleanedGuideProfile.type]: (state) => {
    state.guide = initialState.guide
  },
  [requestedGuideAbout.type]: (state) => {
    setRequested(state.about)
  },
  [receivedGuideAbout.type]: (state, action) => {
    setReceived(state.about)
    state.about.data = action.payload
  },
  [failedGuideAbout.type]: (state) => {
    setFailed(state.about)
  },
  [requestedGuideExperiences.type]: (state) => {
    setRequested(state.experiences)
  },
  [receivedGuideExperiences.type]: (state, action) => {
    setReceived(state.experiences)
    const incoming = action.payload || {}
    const incomingTrips = incoming.trips || []
    const incomingMetadata = incoming.metadata || {}
    const existingData = state.experiences.data || {}
    const existingTrips = existingData.trips || []

    // If page > 1 and we have existing trips, append new trips
    if ((incomingMetadata.page || 1) > 1 && existingTrips.length) {
      state.experiences.data = {
        ...existingData,
        ...incoming,
        trips: existingTrips.concat(incomingTrips),
        metadata: incomingMetadata
      }
    } else {
      // First page or no existing data, replace
      state.experiences.data = incoming
    }
  },
  [failedGuideExperiences.type]: (state) => {
    setFailed(state.experiences)
  },
  [requestedGuideBoats.type]: (state) => {
    setRequested(state.boats)
  },
  [receivedGuideBoats.type]: (state, action) => {
    setReceived(state.boats)
    state.boats.data = action.payload
  },
  [failedGuideBoats.type]: (state) => {
    setFailed(state.boats)
  },
  [requestedGuideLocation.type]: (state) => {
    setRequested(state.location)
  },
  [receivedGuideLocation.type]: (state, action) => {
    setReceived(state.location)
    state.location.data = action.payload
  },
  [failedGuideLocation.type]: (state) => {
    setFailed(state.location)
  },
  [requestedGuideReviews.type]: (state) => {
    setRequested(state.reviews)
  },
  [receivedGuideReviews.type]: (state, action) => {
    setReceived(state.reviews)
    const incoming = action.payload || {}
    const incomingReviews = incoming.reviews || []
    const incomingMetadata = incoming.metadata || {}
    const existingData = state.reviews.data || {}
    const existingReviews = existingData.reviews || []

    if ((incomingMetadata.page || 1) > 1 && existingReviews.length) {
      state.reviews.data = {
        ...existingData,
        ...incoming,
        reviews: existingReviews.concat(incomingReviews),
        metadata: incomingMetadata
      }
    } else {
      state.reviews.data = incoming
    }
  },
  [failedGuideReviews.type]: (state) => {
    setFailed(state.reviews)
  }
})

// Thunk for fetching prompt list
export const getPromptList = (params) =>
  api({
    url: "/text_generator/prompt_list",
    method: "get",
    onStart: requestedPromptList.type,
    onSuccess: receivedPromptList.type,
    onError: failedPromptList.type,
    params
  })

export default guideProfileReducer

export const getGuideProfile = (slug) =>
  api({
    url: `client/guides/${slug}/profile`,
    method: "get",
    onStart: requestedGuideProfile.type,
    onSuccess: receivedGuideProfile.type,
    onError: failedGuideProfile.type
  })

export const getGuideAbout = (slug) =>
  api({
    url: `client/guides/${slug}/about`,
    method: "get",
    onStart: requestedGuideAbout.type,
    onSuccess: receivedGuideAbout.type,
    onError: failedGuideAbout.type
  })

export const getGuideReviews = (id, params = {}) =>
  api({
    url: `/client/guides/${id}/reviews`,
    method: "get",
    onStart: requestedGuideReviews.type,
    onSuccess: receivedGuideReviews.type,
    onError: failedGuideReviews.type,
    params
  })

export const getGuideExperiences = (slug, date = null, page = 1, per_page = 5) => {
  const params = { page, per_page }
  if (date) {
    params.date = date
  }

  return api({
    url: `client/guides/${slug}/guide_experience`,
    method: "get",
    onStart: requestedGuideExperiences.type,
    onSuccess: receivedGuideExperiences.type,
    onError: failedGuideExperiences.type,
    params
  })
}

export const getGuideBoats = (slug) =>
  api({
    url: `client/guides/${slug}/boats`,
    method: "get",
    onStart: requestedGuideBoats.type,
    onSuccess: receivedGuideBoats.type,
    onError: failedGuideBoats.type
  })

export const getGuideLocation = (slug) =>
  api({
    url: `client/guides/${slug}/guide_location`,
    method: "get",
    onStart: requestedGuideLocation.type,
    onSuccess: receivedGuideLocation.type,
    onError: failedGuideLocation.type
  })

export const cleanedGuideProfiles = () => (dispatch) => dispatch(cleanedGuideProfile())
